import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { en } from './locales/en';
import { de } from './locales/de';
import { fr } from './locales/fr';
import { it } from './locales/it';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(duration);
dayjs.extend(relativeTime);

// Capture the visitor's preferred language BEFORE init(). The app is prerendered
// in English and init() runs with lng:'en' so the first client render matches the
// server — but with cookie caching, init() overwrites the saved cookie with 'en'.
// We read the real preference here first; app/root.tsx applies it after hydration.
function detectInitialLanguage(): 'de' | 'en' | 'fr' | 'it' {
  if (typeof document === 'undefined') return 'en';
  const supported = ['de', 'en', 'fr', 'it'];
  const cookieLng = document.cookie.match(/(?:^|;\s*)i18next=([^;]+)/)?.[1];
  const navLngs = typeof navigator !== 'undefined' ? (navigator.languages ?? [navigator.language]) : [];
  // Cookie (the visitor's explicit choice) first, then their ordered navigator
  // languages; return the first base language we actually support.
  for (const candidate of [cookieLng, ...navLngs]) {
    const base = candidate?.split('-')[0];
    if (base && supported.includes(base)) return base as 'de' | 'en' | 'fr' | 'it';
  }
  return 'en';
}
export const initialLanguage = detectInitialLanguage();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    detection: {
      order: ['cookie', 'navigator'],
      caches: ['cookie'],
    },
    supportedLngs: ['de', 'en', 'fr', 'it'],
    debug: false,
    fallbackLng: 'en',
    // Initialize in English to match the prerendered HTML (the build has no
    // request context, so it always renders 'en'). The visitor's real language
    // is detected and applied after hydration in app/root.tsx, so the first
    // client render matches the server and React doesn't throw a hydration
    // mismatch. Without this, the LanguageDetector switches the language during
    // init — before hydration — and de/fr/it visitors get a mismatch.
    lng: 'en',
    interpolation: {
      escapeValue: false,
    },
    resources: {
      en: { translation: en },
      de: { translation: de },
      fr: { translation: fr },
      it: { translation: it },
    },
  });

const localeMap: Record<string, string> = {
  en: 'en-CH',
  de: 'de-CH',
  fr: 'fr-CH',
  it: 'it-CH',
};

const getLocale = () => localeMap[i18n.language] || 'de-CH';

const i18nDateAndTime = (date: Date) => {
  return new Intl.DateTimeFormat(getLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const i18nDate = (date: Date, style: 'full' | 'long' | 'medium' | 'short' = 'short') => {
  return new Intl.DateTimeFormat(getLocale(), {
    dateStyle: style,
  }).format(date);
};

const i18nNumber = (number: number, digits: number = 2) => {
  return new Intl.NumberFormat(getLocale(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(number);
};

const i18nCurrency = (amount: number) => {
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: 'CHF',
  }).format(amount);
};

const i18nDuration = (duration?: number) => {
  if (!duration) return '-';
  return dayjs.duration(duration).humanize();
};

export { i18n, i18nDateAndTime, i18nDate, i18nNumber, i18nCurrency, i18nDuration };
